import * as React from 'react'

import { cn } from '@ps/ds/lib/utils'
import { Separator } from '@ps/ds/ui/separator'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'

function ItemGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="list"
      data-slot="item-group"
      className={cn('group/item-group flex flex-col', className)}
      {...props}
    />
  )
}

function ItemSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="item-separator"
      orientation="horizontal"
      className={cn('my-0', className)}
      {...props}
    />
  )
}

const itemVariants = cva(
  'group/item [&:is(a)]:hover:bg-input-background [&:is(button)]:hover:bg-input-background focus-visible:border-ring focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-offset-2 [&:is(a)]:transition-colors [&:is(button)]:transition-colors flex flex-wrap items-center rounded-lg border border-transparent text-sm outline-none transition-colors duration-100 focus-visible:ring-[3px]',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline: 'border-border',
        fill: 'bg-alabaster-100',
        fillWithShadow: 'bg-alabaster-100 shadow-xs',
        noPadding: 'bg-transparent',
      },
      size: {
        default: 'gap-4 px-4 py-5',
        sm: 'gap-2.5 px-4 py-3',
      },
    },
    compoundVariants: [
      {
        variant: 'noPadding',
        className: 'p-0',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ItemProps = React.ComponentProps<'div'> &
  VariantProps<typeof itemVariants> & { asChild?: boolean }

const Item = React.forwardRef<HTMLDivElement, ItemProps>(
  ({ className, variant = 'default', size = 'default', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div'

    return (
      <Comp
        ref={ref}
        data-slot="item"
        data-variant={variant}
        data-size={size}
        className={cn(itemVariants({ variant, size }), className)}
        {...props}
      />
    )
  },
)

Item.displayName = 'Item'

const itemMediaVariants = cva(
  'flex shrink-0 items-center justify-center gap-2 group-has-[[data-slot=item-description]]/item:translate-y-0.5 group-has-[[data-slot=item-description]]/item:self-start [&_svg]:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        icon: "[&_svg:not([class*='size-'])]:size-6",
        badge: '',
        image: 'overflow-hidden',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function ItemMedia({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemMediaVariants>) {
  return (
    <div
      data-slot="item-media"
      data-variant={variant}
      className={cn(itemMediaVariants({ variant }), className)}
      {...props}
    />
  )
}

function ItemContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="item-content"
      className={cn(
        'flex flex-1 flex-col gap-1.5 [&+[data-slot=item-content]]:flex-none',
        className,
      )}
      {...props}
    />
  )
}

function ItemTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="item-title"
      className={cn(
        'flex w-fit items-center gap-1.5 text-base font-semibold leading-snug',
        className,
      )}
      {...props}
    />
  )
}

function ItemCaption({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'p'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'p'

  return (
    <Comp
      data-slot="item-caption"
      className={cn(
        'text-foreground text-balance text-sm font-normal leading-normal',
        '[&>a:hover]:text-primary [&>a]:underline',
        className,
      )}
      {...props}
    />
  )
}

function ItemDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="item-description"
      className={cn(
        'text-muted-foreground line-clamp-3 text-balance text-sm font-normal leading-normal',
        '[&>a:hover]:text-primary [&>a:hover]:underline',
        className,
      )}
      {...props}
    />
  )
}

const itemActionsVariants = cva('flex items-center gap-2', {
  variants: {
    showOnHover: {
      true: 'opacity-0 focus-within:opacity-100 group-hover/item:opacity-100 group-focus/item:opacity-100 transition-opacity',
      false: '',
    },
  },
  defaultVariants: {
    showOnHover: false,
  },
})

function ItemActions({
  className,
  showOnHover = false,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemActionsVariants>) {
  return (
    <div
      data-slot="item-actions"
      className={cn(itemActionsVariants({ showOnHover }), className)}
      {...props}
    />
  )
}

function ItemHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="item-header"
      className={cn('flex basis-full items-center justify-between gap-2', className)}
      {...props}
    />
  )
}

function ItemFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="item-footer"
      className={cn('flex basis-full items-center justify-between gap-2', className)}
      {...props}
    />
  )
}

export {
  Item,
  ItemActions,
  ItemCaption,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
}
